import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Tabs, Tab, Snackbar, Alert, TextField, Autocomplete, Paper, Divider,
} from '@mui/material'
import { Add, MoreVert, Edit, Block, Sell, Calculate } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listPriceLists, deactivatePriceList, listQuantityDiscounts, listPriceFormulas, calculatePrice,
  type PriceListItem, type ArticleQuantityDiscounts, type PriceFormula, type PriceCalculationResult,
} from '../../api/preturi'
import { listArticles } from '../../api/nomenclatoare'
import { listClients } from '../../api/clienti'
import { errorMessage } from '../../api/client'
import PriceListFormDialog from './PriceListFormDialog'
import PriceListEntriesDialog from './PriceListEntriesDialog'
import QuantityTierFormDialog from './QuantityTierFormDialog'
import PriceFormulaFormDialog from './PriceFormulaFormDialog'

export default function PreturiPage() {
  const [tab, setTab] = useState(0)
  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Comercial</Typography>
        <Typography variant="inherit" color="text.primary">Prețuri și discounturi</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Prețuri și discounturi</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Liste de preț" />
        <Tab label="Discount pe cantitate" />
        <Tab label="Formule de calcul" />
        <Tab label="Calculator" />
      </Tabs>
      {tab === 0 && <PriceListsTab />}
      {tab === 1 && <QuantityTiersTab />}
      {tab === 2 && <FormulasTab />}
      {tab === 3 && <CalculatorTab />}
    </Stack>
  )
}

function PriceListsTab() {
  const [formTarget, setFormTarget] = useState<PriceListItem | null | 'new'>(null)
  const [entriesTarget, setEntriesTarget] = useState<PriceListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: PriceListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['comercial', 'preturi', 'liste'], queryFn: listPriceLists })
  const closeMenu = () => setMenuAnchor(null)

  const deactivateMutation = useMutation({
    mutationFn: deactivatePriceList,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['comercial', 'preturi', 'liste'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  const columns = useMemo<GridColDef<PriceListItem>[]>(() => [
    { field: 'code', headerName: 'Cod', width: 120, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1, minWidth: 200 },
    { field: 'marketSegment', headerName: 'Segment', width: 130, renderCell: (p) => p.row.marketSegment ?? 'Generală' },
    { field: 'entryCount', headerName: 'Prețuri', width: 90, type: 'number' },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activă" size="small" color="success" variant="outlined" />
        : <Chip label="Inactivă" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Listă nouă</Button>
      </Box>
      <Box sx={{ height: 440, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <PriceListFormDialog list={formTarget} onClose={() => setFormTarget(null)} />
      <PriceListEntriesDialog list={entriesTarget} onClose={() => setEntriesTarget(null)} />
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="preturi" onClick={() => { setEntriesTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Sell fontSize="small" /></ListItemIcon><ListItemText>Prețuri</ListItemText>
          </MenuItem>,
          <MenuItem key="deactivate" disabled={!menuAnchor.row.isActive}
            onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}

function QuantityTiersTab() {
  const [formTarget, setFormTarget] = useState<ArticleQuantityDiscounts | null | 'new'>(null)
  const { data, isLoading } = useQuery({ queryKey: ['comercial', 'preturi', 'praguri-cantitate'], queryFn: listQuantityDiscounts })

  const columns = useMemo<GridColDef<ArticleQuantityDiscounts>[]>(() => [
    { field: 'articleCode', headerName: 'Cod articol', width: 140, renderCell: (p) => <code>{p.row.articleCode}</code> },
    { field: 'articleDescription', headerName: 'Denumire', flex: 1, minWidth: 220 },
    {
      field: 'tiers', headerName: 'Praguri', flex: 1.2, minWidth: 260, sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
          {p.row.tiers.map((t, i) => (
            <Chip key={i} size="small" variant="outlined" label={`≥${t.minQuantity}: -${t.discountPercent}%`} />
          ))}
        </Box>
      ),
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={() => setFormTarget(p.row)}><Edit fontSize="small" /></IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Praguri noi</Button>
      </Box>
      <Box sx={{ height: 440, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} getRowId={(r) => r.articleId} columns={columns} loading={isLoading} density="comfortable"
          disableRowSelectionOnClick getRowHeight={() => 'auto'}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <QuantityTierFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
    </Stack>
  )
}

function FormulasTab() {
  const [formTarget, setFormTarget] = useState<PriceFormula | null | 'new'>(null)
  const { data, isLoading } = useQuery({ queryKey: ['comercial', 'preturi', 'formule'], queryFn: listPriceFormulas })

  const columns = useMemo<GridColDef<PriceFormula>[]>(() => [
    { field: 'articleCode', headerName: 'Cod articol', width: 130, renderCell: (p) => <code>{p.row.articleCode}</code> },
    { field: 'articleDescription', headerName: 'Denumire', flex: 1, minWidth: 180 },
    { field: 'rawMaterialPricePerKg', headerName: 'Preț MP (EUR/kg)', width: 140, type: 'number' },
    { field: 'conversionCostPer1000', headerName: 'Conversie/1000', width: 130, type: 'number' },
    { field: 'marginPer1000', headerName: 'Marjă/1000', width: 110, type: 'number' },
    {
      field: 'computedPricePer1000', headerName: 'Preț calculat/1000', width: 150, type: 'number',
      renderCell: (p) => p.row.computedPricePer1000 != null ? p.row.computedPricePer1000.toFixed(2).replace('.', ',') : '—',
    },
    { field: 'effectiveFrom', headerName: 'Valabil de la', width: 110 },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={() => setFormTarget(p.row)}><Edit fontSize="small" /></IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Formulă nouă</Button>
      </Box>
      <Box sx={{ height: 440, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <PriceFormulaFormDialog formula={formTarget} onClose={() => setFormTarget(null)} />
    </Stack>
  )
}

function CalculatorTab() {
  const { data: clients } = useQuery({ queryKey: ['comercial', 'clienti', 'toti'], queryFn: () => listClients() })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles() })

  const [clientId, setClientId] = useState<string | null>(null)
  const [articleId, setArticleId] = useState<string | null>(null)
  const [quantity, setQuantity] = useState('1000')
  const [result, setResult] = useState<PriceCalculationResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => calculatePrice(clientId!, articleId!, Number(quantity)),
    onSuccess: (r) => { setResult(r); setError(null) },
    onError: (e) => { setError(errorMessage(e)); setResult(null) },
  })

  return (
    <Stack spacing={2} sx={{ maxWidth: 640 }}>
      <Stack direction="row" spacing={2}>
        <Autocomplete
          sx={{ flexGrow: 1 }} options={clients?.map((c) => c.id) ?? []}
          getOptionLabel={(id) => clients?.find((c) => c.id === id)?.name ?? id}
          value={clientId} onChange={(_, v) => setClientId(v)}
          renderInput={(params) => <TextField {...params} label="Client" />}
        />
        <Autocomplete
          sx={{ flexGrow: 1 }} options={articles?.map((a) => a.id) ?? []}
          getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
          value={articleId} onChange={(_, v) => setArticleId(v)}
          renderInput={(params) => <TextField {...params} label="Articol" />}
        />
        <TextField type="number" label="Cantitate" value={quantity} onChange={(e) => setQuantity(e.target.value)} sx={{ width: 140 }} />
      </Stack>
      <Button variant="contained" startIcon={<Calculate />} disabled={!clientId || !articleId || !quantity || mutation.isPending}
        onClick={() => mutation.mutate()} sx={{ alignSelf: 'flex-start' }}>
        Calculează
      </Button>

      {error && <Alert severity="error">{error}</Alert>}

      {result && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            {result.steps.map((s, i) => (
              <Stack key={i} direction="row" sx={{ justifyContent: "space-between" }}>
                <Typography variant="body2" color={i === result.steps.length - 1 ? 'text.primary' : 'text.secondary'}
                  sx={{ fontWeight: i === result.steps.length - 1 ? 600 : 400 }}>
                  {s.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: i === result.steps.length - 1 ? 600 : 400 }}>
                  {s.amount != null ? `${s.amount.toFixed(4).replace('.', ',')} ${result.currency}` : '—'}
                </Typography>
              </Stack>
            ))}
            <Divider />
            <Stack direction="row" sx={{ justifyContent: "space-between" }}>
              <Typography variant="body2">Cost</Typography>
              <Typography variant="body2">{result.cost != null ? `${result.cost.toFixed(4).replace('.', ',')} ${result.currency}` : '—'}</Typography>
            </Stack>
            <Stack direction="row" sx={{ justifyContent: "space-between" }}>
              <Typography variant="body2">Marjă</Typography>
              <Typography variant="body2">
                {result.marginAmount != null && result.marginPercent != null
                  ? `${result.marginAmount.toFixed(4).replace('.', ',')} ${result.currency} (${result.marginPercent.toFixed(1).replace('.', ',')}%)` : '—'}
              </Typography>
            </Stack>
            {result.belowMinMargin && <Alert severity="warning">Marja rezultată este sub pragul minim configurat pentru acest articol.</Alert>}
          </Stack>
        </Paper>
      )}
    </Stack>
  )
}
